import { createRootRoute, Outlet } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { resetAnalytics } from '@region13/analytics'
import { api } from '../lib/api'
import { Login } from '../components/Login'
import { AppShell } from '../components/AppShell'
import { Shell } from '../components/Shell'

/** Exported for its unit test (`__root.test.tsx`), which pins that a session
 *  does not outlive its sign-out and that both transitions still work. */
export function RootLayout() {
  const queryClient = useQueryClient()
  // `GET /api/auth/me` always resolves 200 (email-code auth, not Cloudflare
  // Access) — a signed-out visitor gets `{ email: null, ... }` rather than a
  // 401, so "authenticated" is decided by `email` being non-null, not by
  // query success. Shared `['me']` key with @region13/ui's CapabilityProvider
  // (wired in main.tsx) so both dedupe the same fetch.
  const me = useQuery({ queryKey: ['me'], queryFn: api.me, staleTime: 5 * 60_000 })

  if (me.isLoading) {
    return (
      <Shell>
        <p role="status" className="text-center text-sm text-ink-soft py-16">Loading…</p>
      </Shell>
    )
  }

  if (!me.data?.email) {
    // ⚠️ `.clear()`, not `invalidateQueries(['me'])`. Every other query key here
    // is per-person but NAMED as though it were global, so a second sign-in in
    // the same tab renders the FIRST coach's cached data. On THIS surface that
    // includes game cards and team rosters — children's names — which the
    // platform is otherwise careful never to expose. Same defect found on the
    // referee SPA on staging 2026-09-09.
    return (
      <Login
        onSignedIn={() => {
          // ⚠️ NOT `.clear()`. This component is SUBSCRIBED to ['me'], and
          // clearing it mid-sign-in leaves that observer with no data and
          // nothing to refetch — so it renders this Login screen again and
          // reads to the user as "sign-in did nothing". Shipped to staging on
          // 2026-09-09 and locked everyone out. Drop the person-scoped keys
          // and leave ['me'] to refetch.
          queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' })
          void queryClient.invalidateQueries({ queryKey: ['me'] })
        }}
      />
    )
  }

  async function signOut() {
    await api.logout().catch(() => {})
    // A signed-out person must stop being identified, or the next person on a
    // shared browser inherits their PostHog profile.
    resetAnalytics()
    // Drop every cached answer BEFORE seeding the signed-out `me`; order is
    // load-bearing, since clearing after would wipe the seed.
    // ⚠️ SAME PREDICATE AS SIGN-IN, and `.clear()` is wrong here too. An
    // earlier fix used it on the theory that re-seeding ['me'] on the very
    // next line made it safe. It does not: `.clear()` drops the query this
    // component's observer is attached to, and seeding afterwards creates a
    // NEW entry the detached observer never sees — so the UI never returns to
    // the sign-in screen and sign-out appears to do nothing. Keep ['me']
    // alive; only its value changes.
    queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' })
    queryClient.setQueryData(['me'], { email: null, status: 'active', roles: [], capabilities: [] })
  }

  return (
    <AppShell
      email={me.data.email}
      onSignOut={signOut}
      isAdmin={me.data.roles?.includes('superadmin') ?? false}
    >
      <Outlet />
    </AppShell>
  )
}

export const Route = createRootRoute({
  component: RootLayout,
})
